import html2pdf from 'html2pdf.js';

// A4 portrait printable width (210mm - 2 x 10mm margins) at 96 dpi is ~718px.
// Rendering the invoice at this width keeps all 8 internal-invoice columns
// readable in portrait instead of scaling a wide dialog down.
export const INVOICE_PDF_RENDER_WIDTH_PX = 718;
export const INVOICE_PDF_EXPORT_CLASS = 'invoice-pdf-export';

export const INVOICE_PDF_OPTIONS = {
  margin: [10, 10, 10, 10],
  filename: 'document.pdf',
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: { scale: 2, scrollX: 0, scrollY: 0 },
  jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  // Only keep single rows (and the totals block) together. Avoiding breaks inside
  // every element also applied to the whole table, which pushed every line onto
  // page 2 and left page 1 with just the header.
  pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.invoice-pdf-keep-together'] },
};

/**
 * Export an invoice / ticket detail dialog to a portrait A4 PDF.
 * @param {HTMLElement} element   dialog content to print
 * @param {HTMLElement} gridElement scrollable table wrapper (expanded while printing)
 * @param {HTMLElement[]} hiddenElements elements hidden in the PDF (e.g. invoice type toggle)
 */
export function exportInvoicePdf({ element, gridElement, hiddenElements = [] }) {
  if (!element) return Promise.resolve();

  const restore = [];
  const setStyle = (node, styles) => {
    if (!node) return;
    const previous = {};
    Object.keys(styles).forEach((key) => {
      previous[key] = node.style[key];
      node.style[key] = styles[key];
    });
    restore.push(() => Object.assign(node.style, previous));
  };

  setStyle(gridElement, { height: 'auto', overflow: 'visible' });
  setStyle(element, {
    width: `${INVOICE_PDF_RENDER_WIDTH_PX}px`,
    maxWidth: `${INVOICE_PDF_RENDER_WIDTH_PX}px`,
    overflow: 'visible',
  });
  hiddenElements.forEach((node) => setStyle(node, { display: 'none' }));
  element.classList.add(INVOICE_PDF_EXPORT_CLASS);
  restore.push(() => element.classList.remove(INVOICE_PDF_EXPORT_CLASS));

  const restoreAll = () => restore.reverse().forEach((fn) => fn());

  return html2pdf()
    .set(INVOICE_PDF_OPTIONS)
    .from(element)
    .save()
    .then(restoreAll)
    .catch((err) => {
      console.error('Error generating PDF:', err);
      restoreAll();
    });
}
